import { useCallback, useEffect, useRef, useState } from 'react';
import { SCAN_CHUNK_SIZE, uploadScanChunk, startScanJob, getScanJob, getActiveScanJob, cancelScanJob } from '../lib/api';

const POLL_MS = 2000;
const newId = () => `up_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// Drives the async scan pipeline: chunked upload → job start → polling until done/failed.
export const useScanJob = ({ onDone, onError }) => {
  const [progress, setProgress] = useState(null); // { phase: 'upload'|'ocr'|'grading', percent, label }
  const [jobId, setJobId] = useState(null);
  const timer = useRef(null);

  const stopPolling = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const applyJob = useCallback((job) => {
    if (job.status === 'done') {
      stopPolling(); setJobId(null); setProgress(null);
      onDone({ results: job.results, total_pages: job.total_pages, grouped_copies: job.grouped_copies, unmatched_pages: job.unmatched_pages });
      return;
    }
    if (job.status === 'failed' || job.status === 'cancelled') {
      stopPolling(); setJobId(null); setProgress(null);
      onError(job.error || "Échec de l'analyse");
      return;
    }
    const total = job.total_pages || 0;
    const done = job.processed_pages || 0;
    if (total && done >= total) {
      setProgress({ phase: 'grading', percent: 100, label: `Correction des copies (${job.results?.length || 0} corrigée(s))...` });
    } else {
      setProgress({ phase: 'ocr', percent: total ? Math.round((done / total) * 100) : 0,
        label: total ? `Lecture OCR : page ${Math.min(done + 1, total)} / ${total}` : 'Préparation des pages...',
        detail: job.current_source, resumed: job.resumed || 0 });
    }
  }, [onDone, onError]);

  const poll = useCallback((id) => {
    stopPolling();
    setJobId(id);
    const tick = async () => {
      try { applyJob((await getScanJob(id)).data); }
      catch (e) { if (e.response?.status === 404) { stopPolling(); setJobId(null); setProgress(null); } }
    };
    tick();
    timer.current = setInterval(tick, POLL_MS);
  }, [applyJob]);

  // Resume an in-flight job when coming back to the page
  useEffect(() => {
    getActiveScanJob().then(res => { if (res.data.job) poll(res.data.job.job_id); }).catch(() => {});
    return stopPolling;
  }, [poll]);

  const start = async (files) => {
    const totalBytes = files.reduce((s, f) => s + f.size, 0);
    let sent = 0;
    const uploads = [];
    for (const file of files) {
      const uploadId = newId();
      const totalChunks = Math.max(1, Math.ceil(file.size / SCAN_CHUNK_SIZE));
      for (let i = 0; i < totalChunks; i++) {
        const blob = file.slice(i * SCAN_CHUNK_SIZE, (i + 1) * SCAN_CHUNK_SIZE);
        await uploadScanChunk(uploadId, i, totalChunks, blob);
        sent += blob.size;
        setProgress({ phase: 'upload', percent: Math.round((sent / totalBytes) * 100), label: `Envoi des fichiers : ${Math.round((sent / totalBytes) * 100)} %`, detail: file.name });
      }
      uploads.push({ upload_id: uploadId, filename: file.name, content_type: file.type || '' });
    }
    setProgress({ phase: 'ocr', percent: 0, label: 'Préparation des pages...' });
    const res = await startScanJob(uploads);
    poll(res.data.job_id);
  };

  const cancel = async () => {
    if (!jobId) return;
    try { await cancelScanJob(jobId); } catch (e) { onError(e.response?.data?.detail || "Impossible d'annuler"); }
  };

  return { start, cancel, progress, jobId, busy: !!progress || !!jobId };
};
